import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { listTokens, createToken, revokeToken } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';
import { formatDateTimeWithTimezone } from 'utils/date_utils';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';

const MySwal = SwalReact(Swal);

/**
 * Token Management
 * Create and revoke access tokens for external transparency center viewers
 */
export const TokenManagement = ({ config, companyKey }) => {
  const selectedTenant = useSelector(useAuthTenant);
  const companyLocale = LOCALE_MAP[selectedTenant?.defaultLanguage] ?? 'pt-BR';
  const { t } = useTranslation(['transparency', 'common']);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Fetch tokens
  const { data: tokensData, isLoading, error, refetch } = useQuery(
    ['transparencyTokens', companyKey],
    () => listTokens(companyKey),
    {
      enabled: !!companyKey,
      staleTime: 1 * 60 * 1000, // Cache for 1 minute
    }
  );

  // Create token form
  const { control, handleSubmit, reset, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      recipientEmail: '',
      recipientName: '',
      expirationHours: 24,
    },
  });

  // Email validation helper
  const isEmailAllowed = (email, allowedDomains, allowedEmails) => {
    if (!email || !email.includes('@')) return false;
    const domain = email.split('@')[1];
    return allowedDomains.includes(domain) || allowedEmails.includes(email);
  };

  // Create token mutation
  const createMutation = useMutation(
    (data) => createToken(companyKey, data.recipientEmail, data.recipientName, data.expirationHours),
    {
      onSuccess: (response) => {
        setCreateDialogOpen(false);
        reset();
        refetch();
        // Show token in separate dialog
        showTokenDialog(response.data.token);
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:tokenManagement.notifications.generalError'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  // Revoke token mutation
  const revokeMutation = useMutation(
    (tokenId) => revokeToken(companyKey, tokenId),
    {
      onSuccess: () => {
        MySwal.fire({
          title: t('transparency:tokenManagement.notifications.revokeSuccess'),
          text: t('transparency:tokenManagement.notifications.revokeSuccessText'),
          icon: 'success',
        });
        refetch();
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:tokenManagement.notifications.revokeError'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  // Show created token with copy button
  const showTokenDialog = (token) => {
    MySwal.fire({
      title: t('transparency:tokenManagement.notifications.createSuccess'),
      html: (
        <div style={{ margin: '20px 0' }}>
          <p style={{ marginBottom: '10px' }}>{t('transparency:tokenManagement.dialog.tokenCopyHint')}</p>
          <p style={{ color: '#d32f2f', fontWeight: 500 }}>{t('transparency:tokenManagement.dialog.tokenOnce')}</p>
          <textarea
            id="token-string"
            readOnly
            defaultValue={token}
            style={{
              width: '100%',
              height: '120px',
              marginTop: '15px',
              padding: '10px',
              fontFamily: 'monospace',
              fontSize: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              resize: 'none',
            }}
          />
        </div>
      ),
      icon: 'success',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: t('transparency:tokenManagement.actions.copy'),
      cancelButtonText: t('transparency:tokenManagement.actions.close'),
      preConfirm: () => {
        const textarea = document.getElementById('token-string');
        textarea.select();
        document.execCommand('copy');
        return true;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        MySwal.fire(
          t('transparency:tokenManagement.notifications.copied'),
          t('transparency:tokenManagement.notifications.copiedText'),
          'success'
        );
      }
    });
  };

  // Handle create token submit
  const onSubmit = (data) => {
    const allowedDomains = config?.allowedEmailDomains || [];
    const allowedEmails = config?.allowedEmails || [];

    // Validate email against allowlist
    if (!isEmailAllowed(data.recipientEmail, allowedDomains, allowedEmails)) {
      setError('recipientEmail', {
        type: 'manual',
        message: t('transparency:tokenManagement.validation.emailNotAllowed'),
      });
      return;
    }

    clearErrors('recipientEmail');
    createMutation.mutate(data);
  };

  // Handle revoke with confirmation
  const handleRevoke = (tokenId, recipientEmail) => {
    MySwal.fire({
      title: t('transparency:tokenManagement.notifications.revokeTitle'),
      text: t('transparency:tokenManagement.notifications.revokeConfirmText', { email: recipientEmail }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('transparency:tokenManagement.actions.confirmRevoke'),
      cancelButtonText: t('common:buttons.cancel'),
      confirmButtonColor: '#d32f2f',
    }).then((result) => {
      if (result.isConfirmed) {
        revokeMutation.mutate(tokenId);
      }
    });
  };

  // Get token status
  const getTokenStatus = (token) => {
    if (token.revokedAt) return { label: t('transparency:tokenManagement.status.revoked'), color: 'default' };
    if (new Date(token.expiresAt) < new Date()) return { label: t('transparency:tokenManagement.status.expired'), color: 'error' };
    return { label: t('transparency:tokenManagement.status.active'), color: 'success' };
  };

  // Check if token can be revoked
  const canRevoke = (token) => {
    return !token.revokedAt && new Date(token.expiresAt) >= new Date();
  };

  // Loading state
  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          {t('transparency:tokenManagement.error')}
        </Alert>
      </Paper>
    );
  }

  const tokens = tokensData?.data || [];

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('transparency:tokenManagement.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('transparency:tokenManagement.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          {t('transparency:tokenManagement.actions.create')}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {tokens.length === 0 ? (
        <Alert severity="info">
          {t('transparency:tokenManagement.empty')}
        </Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('transparency:tokenManagement.table.recipientEmail')}</TableCell>
                <TableCell>{t('transparency:tokenManagement.table.name')}</TableCell>
                <TableCell>{t('transparency:tokenManagement.table.createdAt')}</TableCell>
                <TableCell>{t('transparency:tokenManagement.table.expiresAt')}</TableCell>
                <TableCell>{t('transparency:tokenManagement.table.lastAccess')}</TableCell>
                <TableCell align="center">{t('transparency:tokenManagement.table.accesses')}</TableCell>
                <TableCell align="center">{t('transparency:tokenManagement.table.status')}</TableCell>
                <TableCell align="center">{t('transparency:tokenManagement.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => {
                const status = getTokenStatus(token);
                return (
                  <TableRow key={token._id}>
                    <TableCell>
                      <Typography variant="body2">{token.recipientEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{token.recipientName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDateTimeWithTimezone(token.createdAt, selectedTenant?.timezone, companyLocale)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDateTimeWithTimezone(token.expiresAt, selectedTenant?.timezone, companyLocale)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {token.lastUsedAt ? formatDateTimeWithTimezone(token.lastUsedAt, selectedTenant?.timezone, companyLocale) : t('transparency:tokenManagement.never')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{token.accessCount}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('transparency:tokenManagement.actions.revoke')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRevoke(token._id, token.recipientEmail)}
                            disabled={!canRevoke(token) || revokeMutation.isLoading}
                            color="error"
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Token Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{t('transparency:tokenManagement.dialog.createTitle')}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Controller
                control={control}
                name="recipientEmail"
                rules={{
                  required: t('transparency:tokenManagement.validation.emailRequired'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('transparency:tokenManagement.validation.emailInvalid'),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('transparency:tokenManagement.fields.recipientEmail')}
                    fullWidth
                    required
                    margin="normal"
                    error={!!errors.recipientEmail}
                    helperText={errors.recipientEmail?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="recipientName"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('transparency:tokenManagement.fields.recipientName')}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                control={control}
                name="expirationHours"
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('transparency:tokenManagement.fields.expiry')}</InputLabel>
                    <Select {...field} label={t('transparency:tokenManagement.fields.expiry')}>
                      <MenuItem value={24}>{t('transparency:tokenManagement.expiry.24')}</MenuItem>
                      <MenuItem value={48}>{t('transparency:tokenManagement.expiry.48')}</MenuItem>
                      <MenuItem value={72}>{t('transparency:tokenManagement.expiry.72')}</MenuItem>
                      <MenuItem value={168}>{t('transparency:tokenManagement.expiry.168')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                {t('transparency:tokenManagement.dialog.createInfo')}
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading}
            >
              {createMutation.isLoading ? <DotsLoader /> : t('transparency:tokenManagement.actions.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Paper>
  );
};
